import * as Icons from "heroicons-react";
import { useEffect, useState } from "react";
import { ProgressBar } from "react-bootstrap";
import { CSVLink } from "react-csv";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  resetImport,
  selectfaildData,
  selectprocessedData,
} from "../../features/importSlice";
import {
  useAddImportCategoryMutation,
  useMasterCategoryQuery,
} from "../../services/categoryApi";
import { useGroupsQuery } from "../../services/groupApi";
import SelectGroupImport from "../Common/CustomSelect/SelectGroupImport";
import Header from "../Common/Header/Header";
import SideBar from "../Common/SideBar/SideBar";
import { signInUser } from "../Utility/Auth";
import CsvImporterCategories from "./csvImportCategories";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api/";
// console.log("BASE_URL category", BASE_URL);

const ImportCategories = () => {
  const navigate = useNavigate();
  const user = signInUser();
  const { aamarId } = user;
  // console.log("aamarId", aamarId);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.importReducer);
  const csvData = data?.csvData;
  const processedData = data?.processedData;
  const faildData = data?.faildData;

  const [isProcess, setIsProcess] = useState(false); // Success Data
  const [pp, setPp] = useState(0); // Process Percent

  const [isImportedDone, setIsImportedDone] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [AddImportCategory] = useAddImportCategoryMutation();

  const { data: categorys } = useMasterCategoryQuery({ aamarId });
  const { data: groups } = useGroupsQuery({ aamarId });
  // console.log("groups", groups);
  // console.log("categorys", categorys);

  // Function to handle input change
  const handleChange = (e, name) => {
    const match = processedData.find((p) => p.name === name);
    const rest = processedData.filter((p) => p.name !== name);
    const updated = {
      ...match,
      name: e,
      isNameUnique: checkCategoryName(e) || false,
    };
    dispatch(selectprocessedData([...rest, updated]));
  };

  const handleImport = async () => {
    if (!processedData || processedData.length === 0) {
      toast.error("No category data to import!");
      return;
    }

    if (isUploading) return;
    setIsUploading(true);
    setIsImportedDone(true);

    const toastId = toast.loading(
      `Importing ${processedData.length} categories...`
    );

    let newProcessData = [...processedData];
    let newFailedData = [];
    let successCount = 0;
    let failedCount = 0;
    const importedNames = new Set();

    for (const category of processedData) {
      const catName = category?.name?.trim();

      if (!catName) {
        failedCount++;
        newFailedData.push({
          name: "",
          company: category?.company || "",
          status: category?.status || "active",
          reason: "Category name is missing",
        });
        continue;
      }

      // Check if duplicate within this batch
      if (importedNames.has(catName.toLowerCase())) {
        failedCount++;
        newFailedData.push({
          name: catName,
          company: category?.company || "",
          status: category?.status || "active",
          reason: "Duplicate category name in file",
        });
        continue;
      }

      // Check if category name already exists in database
      const isDuplicate = await checkNameExists(catName);
      if (isDuplicate) {
        failedCount++;
        newFailedData.push({
          name: catName,
          company: category?.company || "",
          status: category?.status || "active",
          reason: "Category name already exists in database",
        });
        continue;
      }

      // Resolve group ID
      let groupValue = category.group;
      if (groupValue && groups?.length) {
        const found = groups.find(
          (g) =>
            g._id === groupValue ||
            g.name?.trim().toLowerCase() ===
            String(groupValue).trim().toLowerCase() ||
            (category.groupName &&
              g.name?.trim().toLowerCase() ===
              category.groupName.trim().toLowerCase())
        );
        if (found) {
          groupValue = found._id;
        }
      }

      const payload = {
        name: catName,
        code:
          category.code ||
          Math.floor(100000 + Math.random() * 900000).toString(),
        mcId: category.mcId
          ? Number(category.mcId)
          : Number(category.code) ||
          Math.floor(100000 + Math.random() * 900000),
        mc: category.mc || null,
        group: groupValue || null,
        description: category.description || "",
        aamarId: aamarId,
        status: category.status || "active",
      };

      try {
        const result = await AddImportCategory(payload);
        if (result?.data && !result?.error) {
          successCount++;
          importedNames.add(catName.toLowerCase());
          newProcessData = newProcessData.map((p) =>
            p.name === category.name ? { ...p, isImport: true } : p
          );
        } else {
          failedCount++;
          const errMsg =
            result?.error?.data?.message ||
            result?.error?.data?.error ||
            "Failed to save category";
          newFailedData.push({
            name: catName,
            company: category?.company || "",
            status: category?.status || "active",
            reason: errMsg,
          });
        }
      } catch (err) {
        failedCount++;
        newFailedData.push({
          name: catName,
          company: category?.company || "",
          status: category?.status || "active",
          reason: err?.message || "Server error while saving category",
        });
      }
    }

    dispatch(selectfaildData(newFailedData));
    dispatch(selectprocessedData(newProcessData));
    setIsUploading(false);

    toast.dismiss(toastId);
    if (successCount > 0 && failedCount === 0) {
      toast.success(
        `Successfully imported ${successCount} ${successCount === 1 ? "category" : "categories"
        }!`
      );
    } else if (successCount > 0 && failedCount > 0) {
      toast.success(
        `Imported ${successCount} categories. ${failedCount} failed.`
      );
    } else if (failedCount > 0) {
      toast.error(
        `Failed to import ${failedCount} categories. Check failed data.`
      );
    }
  };

  const getGroupIdbyName = (name) => {
    if (groups && name) {
      const result = groups?.find(
        (group) =>
          group?.name?.trim().toLowerCase() === name?.trim().toLowerCase()
      );
      return result?._id || null;
    } else {
      return null;
    }
  };

  // check Name
  const checkNameExists = async (name) => {
    try {
      if (!name) return false;
      const cleanName = encodeURIComponent(name.trim());
      const response = await fetch(
        `${BASE_URL}category/checkName/${aamarId}/${cleanName}`
      );
      if (!response.ok) {
        return (
          categorys?.some(
            (c) => c?.name?.trim().toLowerCase() === name.trim().toLowerCase()
          ) || false
        );
      }
      const data = await response.json();
      return Boolean(data?.exists);
    } catch (error) {
      console.error("Error checking category name:", error);
      return (
        categorys?.some(
          (c) => c?.name?.trim().toLowerCase() === name?.trim().toLowerCase()
        ) || false
      );
    }
  };

  // TODO:: Generate Article_code
  const generateCode = async () => {
    try {
      let code;
      let isUnique = false;

      while (!isUnique) {
        code = Math.floor(100000 + Math.random() * 900000).toString();
        isUnique = !(await checkCodeExists(code));
      }

      return code;
    } catch (error) {
      console.error("Error generating Article Code:", error);
      return Math.floor(100000 + Math.random() * 900000).toString();
    }
  };

  const sanitizeMc = (mc) => {
    return mc && !isNaN(mc) ? Number(mc) : null;
  };

  const processCsvData = async (csvData) => {
    dispatch(selectprocessedData([]));
    dispatch(selectfaildData([]));
    let oldData = [];

    let i = 1;
    for (const product of csvData) {
      const catName =
        product?.name ||
        product?.category ||
        product?.Category ||
        product?.Name ||
        "";
      const cleanName = String(catName).trim();
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      const groupMatch = groups?.find(
        (g) =>
          g?.name?.trim().toLowerCase() ===
          product?.group?.toString().trim().toLowerCase()
      );

      const newProduct = {
        name: cleanName,
        code: code,
        mcId: Number(code),
        mc: sanitizeMc(product?.mc),
        group: groupMatch ? groupMatch._id : null,
        groupName: groupMatch ? groupMatch.name : product?.group || "",
        description: product?.description || "",
        aamarId: aamarId,
        status: product?.status || "active",

        order: i++,
        readyToIport: true,
        isImport: false,
        isEditable: checkCategoryName(cleanName) || false,
        isNameUnique: checkCategoryName(cleanName) || false,
      };

      oldData.push(newProduct);
    }

    dispatch(selectprocessedData(oldData));
  };

  useEffect(() => {
    // console.log("CSV", csvData);
    // console.log("csvData Changes");
    if (csvData?.length > 0) {
      if (csvData?.length !== processedData?.length) {
        csvData?.length > 0 && processCsvData(csvData);
      }
    }
  }, [csvData]);

  useEffect(() => {
    // console.log("CSV", csvData);
    // console.log("csvData Changes");
    if (csvData?.length > 0) {
      if (csvData?.length !== processedData?.length) {
        csvData?.length > 0 && processCsvData(csvData);
      }
    }
  }, []);

  useEffect(() => {
    setIsProcess(true);
    setPp(0);
    // let progress = 0;
    const processPercent = (100 / csvData?.length) * processedData?.length;

    // console.log("Process Percent");
    setPp(processPercent || 0);
    // setIsProcess(true);
    // csvData.length > 0 && processCsvData(csvData);
  }, [processedData]);

  // console.log("processedData", processedData);

  // const getcategoryIdbyName = (name) => {
  //   if (categorys) {
  //     const result = categorys?.find(
  //       (grp) => grp?.name?.toLowerCase() === name?.toLowerCase()
  //     );
  //     // console.log(result);
  //     return result?._id;
  //   } else {
  //     return null;
  //   }
  // };

  //   const checkcategoryName = (name) => {
  //     if (categorys) {
  //       const result = categorys?.find(
  //         (category) => category?.name?.toLowerCase() === name?.toLowerCase()
  //       );
  //       console.log("category",categorys, name, result);
  //       return result?.name;
  //     } else {
  //       return null;
  //     }
  //   };

  const checkCategoryName = (name) => {
    return (
      !categorys?.some(
        (category) => category?.name?.toLowerCase() === name?.toLowerCase()
      ) || false
    );
  };

  // check code
  const checkCodeExists = async (code) => {
    return (
      categorys?.some(
        (category) => category?.code?.toLowerCase() === code?.toLowerCase()
      ) || false
    );
  };

  // const getCategoryIdbyName = (name) => {
  //   if (categorys) {
  //     const result = categorys?.find(
  //       (grp) => grp?.name?.toLowerCase() === name?.toLowerCase()
  //     );
  //     // console.log(result);
  //     return result?._id;
  //   } else {
  //     return null;
  //   }
  // };

  //   console.log("PP", pp);
  return (
    <div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-2">
            <SideBar />
          </div>
          <div className="col-md-10">
            <Header title="Import Category" />
            <div className="row">
              <div className="col-md-12 table-responsive">
                {csvData?.length <= 0 ? (
                  <div
                    className="d-flex align-items-center justify-content-center vh-100"
                    style={{ marginTop: "-80px" }}
                  >
                    <div className="col-6">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <p className="m-0 p-0 fw-bold">Import Category</p>
                          <p className="m-0 p-0">
                            <small>
                              Please check the format before uploading
                              <a
                                href="/importSample/importCategorySample.csv"
                                download
                                className="d-inline-flex align-items-center text-decoration-none text-opacity-75 text-primary gap-1"
                                style={{ transition: "color 0.3s ease-in-out" }}
                              >
                                <Icons.Download size="15" />
                                Download
                              </a>{" "}
                              the sample file
                              {/* File to check the format before uploading. */}
                            </small>
                          </p>
                        </div>
                      </div>
                      <div>
                        <CsvImporterCategories />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12">
                      <p className="d-flex justify-content-between p-2">
                        Total Product: {csvData?.length}{" "}
                        <span className="input-category-text">
                          <span
                            className="input-category-text gap-2"
                            id="basic-addon1"
                          >
                            {isImportedDone && faildData?.length > 0 && (
                              // TODO:: MAKE THIS BUTTON EXPOrt FAILD

                              <CSVLink
                                data={faildData}
                                filename="failed_products.csv"
                                className="btn btn-dark"
                              >
                                <Icons.Download size="18" /> Export Failed Data
                              </CSVLink>
                            )}
                            {!isImportedDone && (
                              <button
                                onClick={() => handleImport()}
                                className="btn btn-block btn-dark"
                                style={{ zIndex: 0 }}
                                disabled={isUploading}
                              >
                                <Icons.Upload size="18" />{" "}
                                {isUploading ? "Importing..." : "Import Category"}
                              </button>
                            )}
                            {!isImportedDone && (
                              <button
                                onClick={() => {
                                  processCsvData(csvData);
                                  setIsImportedDone(false);
                                }}
                                className="btn btn-block btn-outline-dark"
                                style={{ zIndex: 0 }}
                              >
                                <Icons.Refresh size="18" /> Reload
                              </button>
                            )}

                            <button
                              onClick={() => {
                                dispatch(resetImport());
                                setIsImportedDone(false);
                              }}
                              className="btn btn-block btn-outline-dark"
                              style={{ zIndex: 0 }}
                            >
                              {!isImportedDone ? "Cancle" : "Import Again"}{" "}
                              <Icons.X size="18" />
                            </button>
                            {isImportedDone && (
                              <button
                                onClick={() => {
                                  dispatch(resetImport());
                                  setIsImportedDone(false);
                                  navigate("/category");
                                }}
                                className="btn btn-block btn-outline-success"
                                style={{ zIndex: 0 }}
                              >
                                <Icons.CheckCircle size="18" /> Done
                              </button>
                            )}
                          </span>
                        </span>
                      </p>
                    </div>
                    {!isProcess ? (
                      <div className="col-12 col-md-6 offset-md-3">
                        <div
                          className="d-flex flex-column align-items-left justify-content-center w-100"
                          style={{ height: "60vh" }}
                        >
                          <h3>Processing Data {pp}%</h3>
                          <div className="w-100">
                            <ProgressBar
                              animated
                              striped
                              variant="success"
                              now={pp}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="col-12 table-responsive">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Name</th>
                              <th>Description</th>
                              <th>Code</th>
                              <th>Group</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {processedData.length > 0 ? (
                              processedData
                                ?.slice()
                                ?.sort((a, b) => a.order - b.order)
                                ?.map((item, index) => (
                                  <tr key={index}>
                                    <th>{index + 1}</th>
                                    <td>
                                      {item?.isEditable ? (
                                        item?.name
                                      ) : (
                                        <input
                                          className="form-control"
                                          value={item?.name}
                                          onChange={(e) =>
                                            handleChange(
                                              e.target.value,
                                              item?.name
                                            )
                                          } // If needed for editing
                                          style={{
                                            border: `1px solid ${item?.isNameUnique ? "" : "tomato"
                                              }`,
                                            outline: "none",
                                            width: "200px", // Adjust the width as needed
                                            padding: "5px", // To ensure text fits comfortably
                                          }}
                                        />
                                      )}
                                    </td>

                                    <td>{item?.description}</td>
                                    <td>{item?.code}</td>

                                    <td
                                      style={{
                                        width: "200px",
                                      }}
                                    >
                                      {/* {console.log("group name", item.group)} */}
                                      {groups?.length > 0 ? (
                                        <SelectGroupImport
                                          processedData={processedData}
                                          groups={groups}
                                          code={item?.code}
                                          gp={item?.groupName || item?.group}
                                        />
                                      ) : (
                                        item?.name
                                      )}
                                    </td>

                                    <td
                                      className={
                                        item?.isEditable
                                          ? item?.isNameUnique
                                            ? "text-success"
                                            : item?.isImport
                                              ? item?.isNameUnique
                                                ? "text-danger"
                                                : "text-success"
                                              : "text-success"
                                          : item?.isNameUnique
                                            ? "text-success"
                                            : "text-danger"
                                      }
                                    >
                                      {item?.isEditable
                                        ? item?.isNameUnique
                                          ? "Ready"
                                          : item?.isImport
                                            ? item?.isNameUnique
                                              ? "Failed"
                                              : "Ready"
                                            : "Ready"
                                        : item?.isNameUnique
                                          ? item?.isImport
                                            ? "Done"
                                            : "Ready"
                                          : "Failed"}
                                    </td>
                                  </tr>
                                ))
                            ) : (
                              <tr>
                                <td colSpan="15" className="text-center">
                                  No Data Available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <Toaster position="bottom-right" />
        </div>
      </div>
    </div>
  );
};

export default ImportCategories;
